import {
  AnimationClip,
  InterpolateLinear,
  Matrix4,
  NormalAnimationBlendMode,
  Quaternion,
  QuaternionKeyframeTrack,
  Vector3,
  VectorKeyframeTrack,
} from 'three';

import { Camera } from '@react-three/fiber';
import { CameraTravelPath } from '@spazious/dollhouse-three';

/**
 * Creates animation clip for camera to follow a path.
 *
 *
 * @see https://github.com/nytimes/three-story-controls/blob/f839ff3c87302e6e56af221949dbe5c64f4d1a39/src/camerahelper/index.ts#L247
 * @param camera
 * @param pathTravel
 * @param duration
 * @param eyeHeight
 * @param useSlerp
 */
export const createCameraAnimation = (
  camera: Camera,
  pathTravel: CameraTravelPath,
  maxDuration?: number
) => {
  const positionValues: number[] = [];
  const quaternionValues: number[] = [];
  const times: number[] = [];

  const tmpPosition = new Vector3();
  const tmpQuaternion = new Quaternion();
  const tmpMatrix = new Matrix4();

  const secondsPerMeter = 0.25;

  let currentTime = 0;

  for (let i = 0; i < pathTravel.nodes.length; i++) {
    const node = pathTravel.nodes[i];
    tmpPosition.copy(node.eye);
    tmpQuaternion.setFromRotationMatrix(tmpMatrix.lookAt(node.eye, node.target, camera.up));
    tmpPosition.toArray(positionValues, positionValues.length);
    tmpQuaternion.toArray(quaternionValues, quaternionValues.length);
    if (i === 0) {
      times.push(0);
    } else {
      const prevNode = pathTravel.nodes[i - 1];
      const dist = prevNode.eye.distanceTo(node.eye);
      const curAnimDuration = Math.max(2, Math.min(4, dist * secondsPerMeter));
      currentTime += curAnimDuration;
      times.push(currentTime);
    }
  }

  return new AnimationClip(
    'CameraPath',
    maxDuration ? Math.min(maxDuration, currentTime) : undefined,
    [
      new VectorKeyframeTrack('.position', times, positionValues, InterpolateLinear),
      new QuaternionKeyframeTrack('.quaternion', times, quaternionValues, InterpolateLinear),
    ],
    NormalAnimationBlendMode
  );
};
